import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, AlertTriangle, RefreshCw, Power } from 'lucide-react';
import { statistik, type StatEinbau } from '../api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Einschalten, Einbauen, Nachsehen.
 *
 * ══ WARUM DAS EINSCHALTEN EINE BESTAETIGUNG VERLANGT ═══════════════════════
 *
 * Der Zaehler ist in einer Minute eingebaut, der Datenschutzabschnitt kostet
 * einen Handgriff mehr — und genau der ist die Voraussetzung dafuer, dass die
 * Messung zulaessig ist. Ein Schalter, der beides trennt, produziert Seiten,
 * die zaehlen, ohne es zu sagen. Deshalb steht der Text hier, und deshalb muss
 * jemand bestaetigen, dass er ihn einbaut.
 */

function Kopierbar({ text, label }: { text: string; label: string }) {
  const { t } = useTranslation();
  const [kopiert, setKopiert] = useState(false);

  async function kopieren() {
    try {
      await navigator.clipboard.writeText(text);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 1500);
    } catch {
      // Ohne Zwischenablage-Recht bleibt der Text im Feld und laesst sich von
      // Hand markieren. Ein Fehlerhinweis waere hier mehr Laerm als Hilfe.
    }
  }

  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-slate-600">{label}</span>
        <button
          type="button"
          onClick={kopieren}
          className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50"
        >
          {kopiert ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
          {kopiert ? t('webstat.copied') : t('webstat.copy')}
        </button>
      </div>
      <pre className="max-h-64 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-2 text-[11px] leading-relaxed text-slate-700">
        {text}
      </pre>
    </div>
  );
}

/** Wieviele Tage liegt ein Zeitpunkt zurück? */
function tageSeit(wert: string | null): number | null {
  if (!wert) return null;
  return Math.floor((Date.now() - new Date(wert).getTime()) / 86_400_000);
}

/**
 * Was die Nachschau auf der Webseite ergeben hat.
 *
 * Getrennt von den Zahlen, weil es eine andere Frage beantwortet: nicht „wie
 * viele kamen", sondern „wird ueberhaupt noch gemessen, und darf es das".
 */
export function EinbauHinweis({ einbau }: { einbau: StatEinbau }) {
  const { t } = useTranslation();
  const probleme: string[] = [];

  if (!einbau.tagGefunden) {
    const tage = tageSeit(einbau.tagFehltSeit);
    probleme.push(
      tage !== null && tage > 0
        ? t('webstat.tagMissingSince', { days: tage })
        : t('webstat.tagMissing'),
    );
  }

  if (!einbau.datenschutzGefunden) {
    const tage = tageSeit(einbau.datenschutzFehltSeit);
    probleme.push(
      tage !== null && tage > 0
        ? t('webstat.privacyMissingSince', { days: tage })
        : t('webstat.privacyMissing'),
    );
  }

  if (probleme.length === 0) return null;

  return (
    <div className="mb-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
      <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-500" />
      <div>
        <p className="mb-1 font-medium">{t('webstat.checkTitle')}</p>
        <ul className="space-y-0.5">
          {probleme.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
        {einbau.gepruefetAm && (
          <p className="mt-1 text-[11px] text-amber-700">
            {t('webstat.lastChecked', {
              when: new Date(einbau.gepruefetAm).toLocaleString(),
            })}
          </p>
        )}
      </div>
    </div>
  );
}

export default function StatistikEinrichtung({ instanceId }: { instanceId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const darfSchalten = user?.role === 'ADMIN' || user?.role === 'OPERATIONS';

  const [bestaetigt, setBestaetigt] = useState(false);
  const [fehler, setFehler] = useState('');
  const [nachschauBefund, setNachschauBefund] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['statistik', instanceId, 'einrichtung'],
    queryFn: () => statistik.einrichtung(instanceId),
  });

  const aktualisieren = () => {
    qc.invalidateQueries({ queryKey: ['statistik', instanceId] });
  };

  const einschalten = useMutation({
    mutationFn: () => statistik.einschalten(instanceId),
    onSuccess: aktualisieren,
    onError: (err: unknown) => {
      setFehler(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          t('webstat.switchOnFailed'),
      );
    },
  });

  const ausschalten = useMutation({
    mutationFn: () => statistik.ausschalten(instanceId),
    onSuccess: aktualisieren,
  });

  const nachschau = useMutation({
    mutationFn: () => statistik.nachschau(instanceId),
    onSuccess: (ergebnis) => {
      setNachschauBefund(ergebnis.befund);
      aktualisieren();
    },
    onError: (err: unknown) => {
      setNachschauBefund(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          t('webstat.checkFailed'),
      );
    },
  });

  if (isLoading) return <div className="h-24 animate-pulse rounded bg-slate-100" />;
  if (!data?.moeglich) {
    return <p className="text-sm text-slate-500">{data?.grund}</p>;
  }

  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">{t('webstat.setup')}</h3>
          <p className="text-xs text-slate-500">
            {data.eingeschaltet ? t('webstat.switchedOn') : t('webstat.switchedOff')}
          </p>
        </div>
        {darfSchalten && (
          <div className="flex gap-2">
            {data.eingeschaltet && (
              <button
                type="button"
                onClick={() => nachschau.mutate()}
                disabled={nachschau.isPending}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                title={t('webstat.checkNowHint')}
              >
                <RefreshCw size={12} className={nachschau.isPending ? 'animate-spin' : ''} />
                {nachschau.isPending ? t('webstat.checking') : t('webstat.checkNow')}
              </button>
            )}
            {data.eingeschaltet ? (
              <button
                type="button"
                onClick={() => ausschalten.mutate()}
                disabled={ausschalten.isPending}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                <Power size={12} /> {t('webstat.switchOff')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setFehler('');
                  einschalten.mutate();
                }}
                disabled={!bestaetigt || einschalten.isPending}
                className="inline-flex items-center gap-1 rounded-lg bg-brand-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-40"
                title={bestaetigt ? undefined : t('webstat.switchOnBlocked')}
              >
                <Power size={12} /> {t('webstat.switchOn')}
              </button>
            )}
          </div>
        )}
      </div>

      {fehler && (
        <div className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
          {fehler}
        </div>
      )}

      {nachschauBefund && (
        <div className="mb-3 rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
          {nachschauBefund}
        </div>
      )}

      {data.scriptZeile && (
        <Kopierbar
          label={data.eingeschaltet ? t('webstat.scriptLine') : t('webstat.scriptLinePreview')}
          text={data.scriptZeile}
        />
      )}

      {data.eingeschaltet && (
        <p className="mb-3 text-[11px] leading-relaxed text-slate-500">
          {t('webstat.scriptLineHint')}
        </p>
      )}

      {data.datenschutzText && (
        <Kopierbar
          label={t('webstat.privacyText')}
          text={data.datenschutzText}
        />
      )}

      {!data.eingeschaltet && darfSchalten && (
        <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={bestaetigt}
            onChange={(e) => setBestaetigt(e.target.checked)}
            className="mt-0.5"
          />
          <span>{t('webstat.confirmPrivacy')}</span>
        </label>
      )}
    </div>
  );
}
